// @spec TSK-STR-001
// @spec TSK-STR-002
// @spec AI-STR-023

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SmartPrioritizeButton } from '@/features/ai/smart';
import {
  ApprovalInboxList,
  hasActiveFilters,
  TaskFiltersComponent,
  TaskList,
  useMyTasks,
  useTaskFiltersStore,
} from '@/features/tasks';
import { isUnifiedApprovalsInboxEnabled } from '@/features/tasks/lib/feature-flags';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/tasks/my')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.myTasks'),
  }),
  component: MyTasksPage,
});

/**
 * My Tasks page - Personal task list view.
 *
 * Displays all tasks assigned to the current user with:
 * - Filtering by status, priority, and source module
 * - Sorting by due date, priority, or created date
 * - Pagination with "Load more" button
 * - Visual indicators for overdue tasks
 */
function MyTasksPage() {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const filters = useTaskFiltersStore.use.filters();
  const setStatus = useTaskFiltersStore.use.setStatus();
  const setPriority = useTaskFiltersStore.use.setPriority();
  const setSourceModule = useTaskFiltersStore.use.setSourceModule();
  const setSort = useTaskFiltersStore.use.setSort();
  const clearFilters = useTaskFiltersStore.use.clearFilters();

  const {
    tasks,
    isLoading,
    error,
    hasMore,
    totalCount,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useMyTasks({ filters });

  const handleNavigate = (taskId: string) => {
    void navigate({ to: '/tasks/$taskId', params: { taskId } });
  };

  const handleSortChange = (sortBy: typeof filters.sortBy, sortOrder: typeof filters.sortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const hasFiltersActive = hasActiveFilters(filters);
  const showApprovals = isUnifiedApprovalsInboxEnabled;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('page.title')}</h1>
            <p className="mt-1 font-medium text-foreground/70">
              {isLoading ? (
                t('page.loading')
              ) : error ? (
                t('page.unableToLoad')
              ) : (
                t('page.tasksAssigned', { count: totalCount ?? 0 })
              )}
            </p>
          </div>
          <SmartPrioritizeButton scope="my_tasks" disabled={isLoading} />
        </header>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-sm text-destructive mb-4">
              {error instanceof Error ? error.message : t('page.failedToLoad')}
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t('page.tryAgain')}
            </Button>
          </div>
        )}

        {!error && (
          <TaskFiltersComponent
            filters={filters}
            onStatusChange={setStatus}
            onPriorityChange={setPriority}
            onSourceModuleChange={setSourceModule}
            onSortChange={handleSortChange}
            onClearFilters={clearFilters}
          />
        )}

        {!error && (
          <TaskList
            tasks={tasks}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={fetchNextPage}
            isLoadingMore={isFetchingNextPage}
            onNavigate={handleNavigate}
            hasFilters={hasFiltersActive}
            onClearFilters={clearFilters}
          />
        )}

        {showApprovals ? <ApprovalInboxList /> : null}
      </div>
    </div>
  );
}
